import { Database, FileDown, FileText, Globe, MapPin, Video } from "lucide-react";
import { motion } from "motion/react";
import { useEffect, useState } from "react";
import plannerImage from "../assets/planner-screenshot.webp";
import { DEMO_URL, REGISTER_URL } from "../constants";
import { FeatureCards } from "./FeatureCards";

const options = [
	{
		text: "travel websites",
		icon: FileText,
		leftCard: { title: "Travel Websites", subtitle: "Branded trip pages" },
		rightCard: { title: "Custom Branding", subtitle: "Match your style" },
	},
	{
		text: "full itineraries",
		icon: MapPin,
		leftCard: { title: "Day-by-Day Planning", subtitle: "Every detail organized" },
		rightCard: { title: "Smart Scheduling", subtitle: "Optimized timing" },
	},
	{
		text: "travel reels",
		icon: Video,
		leftCard: { title: "Stunning Visuals", subtitle: "Auto-generated videos" },
		rightCard: { title: "Social Ready", subtitle: "Share instantly" },
	},
	{
		text: "pdf exports",
		icon: FileDown,
		leftCard: { title: "Print-Ready", subtitle: "Beautiful documents" },
		rightCard: { title: "Client Access", subtitle: "Easy sharing" },
	},
	{
		text: "embed maps",
		icon: Globe,
		leftCard: { title: "Interactive Maps", subtitle: "Embed anywhere" },
		rightCard: { title: "Live Updates", subtitle: "Real-time sync" },
	},
	{
		text: "your travel library",
		icon: Database,
		leftCard: { title: "Store Everything", subtitle: "All destinations & trips" },
		rightCard: { title: "Easy Access", subtitle: "Find anything instantly" },
	},
];

export function HeroSection() {
	const [currentIndex, setCurrentIndex] = useState(0);
	const [displayText, setDisplayText] = useState("");
	useEffect(() => {
		const currentText = options[currentIndex].text;
		let charIndex = 0;

		setDisplayText("");

		const typingInterval = setInterval(() => {
			if (charIndex < currentText.length) {
				setDisplayText(currentText.slice(0, charIndex + 1));
				charIndex++;
			} else {
				clearInterval(typingInterval);
			}
		}, 80);

		const changeTimeout = setTimeout(() => {
			setCurrentIndex((prev) => (prev + 1) % options.length);
		}, 2500);

		return () => {
			clearInterval(typingInterval);
			clearTimeout(changeTimeout);
		};
	}, [currentIndex]);

	return (
		<section className="bg-[#0A0E27] min-h-[calc(100vh-73px)] relative overflow-hidden">
			{/* Background gradient effects */}
			<div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-transparent to-blue-900/20" />
			<div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl" />
			<div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl" />

			<div className="container mx-auto px-6 py-20 relative">
				<div className="max-w-5xl mx-auto text-center">
					<div className="mb-16 ">
						{/* Badge */}
						<motion.div
							initial={{ opacity: 0, y: -20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6 }}
							className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-900/60 to-purple-800/60 border border-purple-400/50 mb-8 shadow-lg shadow-purple-500/50 backdrop-blur-sm"
						>
							<span className="relative">
								<span className="absolute inset-0 blur-md bg-white/40"></span>
								<span className="relative text-white font-semibold text-sm">Now live: Stippl Business</span>
							</span>
						</motion.div>

						{/* Subtitle */}
						<motion.p
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							transition={{ duration: 0.6, delay: 0.2 }}
							className="text-white/80 text-base mb-6 font-medium"
						>
							The travel planner that works like magic
						</motion.p>

						<div className="h-[310px] md:h-auto">
							<h1 className="text-4xl md:text-5xl mb-5 text-white font-bold" style={{ fontFamily: "Poppins, sans-serif" }}>
								Build{" "}
								<span className="inline-block min-w-[200px] md:min-w-[350px] text-left">
									<span className="inline-block bg-gradient-to-r from-[#D4C5F9] to-[#8B6CE8] bg-clip-text text-transparent">
										{displayText}
										<motion.span
											animate={{ opacity: [1, 0] }}
											transition={{ duration: 0.8, repeat: Infinity, repeatType: "reverse" }}
											className="inline-block w-0.5 h-10 md:h-12 bg-[#D4C5F9] ml-1 align-middle"
										/>
									</span>
								</span>
							</h1>
							<h1 className="text-4xl md:text-5xl mb-6 text-white font-bold" style={{ fontFamily: "Poppins, sans-serif" }}>
								in minutes
							</h1>

							<p className="text-base md:text-lg text-white/70 max-w-2xl mx-auto mb-8">
								From day-by-day itineraries to stunning client proposals, create professional travel plans that win more bookings.
							</p>
						</div>
						<div className="flex flex-wrap items-center justify-center gap-4">
							<a
								href={DEMO_URL}
								target="_blank"
								rel="noopener noreferrer"
								className="inline-flex items-center justify-center rounded-md text-base px-6 md:px-10 py-2.5 font-semibold bg-[#8B5CF6] hover:bg-[#7C3AED] text-white shadow-lg shadow-[#8B5CF6]/30 hover:shadow-xl hover:shadow-[#8B5CF6]/40 transition-all duration-300"
							>
								Book a demo
							</a>
							<a
								href={REGISTER_URL}
								target="_blank"
								rel="noopener noreferrer"
								className="inline-flex items-center justify-center rounded-md text-base px-6 md:px-10 py-2.5 font-semibold bg-white hover:bg-white/90 text-[#0A0E27] shadow-lg shadow-white/20 transition-all duration-300"
							>
								Start free trial
							</a>
						</div>
					</div>

					<div className="flex justify-center items-center mt-10 md:mt-20 relative">
						{/* Central Planner - Always visible */}
						<div className="relative z-10">
							<div className="w-full max-w-[900px] rounded-2xl shadow-2xl overflow-hidden bg-white">
								<img src={plannerImage} alt="Travel Planner" className="w-full h-full" />
							</div>

							{/* Feature Card overlay - hidden on mobile */}
							<div className="block">
								<FeatureCards currentFeature={options[currentIndex].text} />
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
